/* External Deps */
import { tokens } from "@/tokens";
import { FC, ReactElement } from "react";
import { Box, Typography, Card, CardContent, Divider, Alert, Chip } from "@mui/material";

/* Internal Deps */
import { IconImage } from "@/components";
import { report_cross } from "@/constants";

// Type guard to check if details are CertifierDetails
const isCertifierDetails = (details: CertifierDetails | ExporterDetails | ProducerDetails | ImporterDetails): details is CertifierDetails => {
  return 'yourName' in details || 'title' in details;
};

interface ExcelProcessResponse {
  success: boolean;
  data?: CertificateExtractedData;
  message: string;
}

interface ResponseDisplayProps {
  response: ExcelProcessResponse;
}

const ResponseDisplay: FC<ResponseDisplayProps> = ({ response }): ReactElement => {
  if (!response.success) {
    return (
      <Box sx={{ mt: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Processing Failed
          </Typography>
          <Typography variant="body2">
            {response.message}
          </Typography>
        </Alert>
      </Box>
    );
  }

  const { data } = response;

  if (!data) {
    return (
      <Box sx={{ mt: 3 }}>
        <Alert severity="warning">
          <Typography variant="body2">
            No data was extracted from the Excel file.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Helper function to render party details
  const renderPartyDetails = (title: string, details: CertifierDetails | ExporterDetails | ProducerDetails | ImporterDetails | undefined) => {
    if (!details) return null;
    
    const hasData = Object.values(details).some(value => value);
    if (!hasData) return null;

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary">
            {title}
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 1 }}>
            {isCertifierDetails(details) && details.yourName && (
              <>
                <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.bold }}>Your Name:</Typography>
                <Typography variant="body2">{details.yourName}</Typography>
              </>
            )}
            {isCertifierDetails(details) && details.title && (
              <>
                <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.bold }}>Title:</Typography>
                <Typography variant="body2">{details.title}</Typography>
              </>
            )}
            {details.address && (
              <>
                <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.bold }}>Address:</Typography>
                <Typography variant="body2">{details.address}</Typography>
              </>
            )}
            {details.countryAndPostalCode && (
              <>
                <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.bold }}>Country & Postal Code:</Typography>
                <Typography variant="body2">{details.countryAndPostalCode}</Typography>
              </>
            )}
            {details.phone && (
              <>
                <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.bold }}>Phone:</Typography>
                <Typography variant="body2">{details.phone}</Typography>
              </>
            )}
            {details.email && (
              <>
                <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.bold }}>Email:</Typography>
                <Typography variant="body2">{details.email}</Typography>
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconImage src={report_cross} size="24px" />
        Extracted Certificate Data
      </Typography>
      
      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="body2">
          {response.message}
        </Typography>
      </Alert>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Certifier Information */}
        {data.certifier && (
          <Card sx={{ bgcolor: tokens.status.successBg }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Certifier Information
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Certifier Type:</strong>{' '}
                <Chip 
                  label={data.certifier} 
                  size="small" 
                  color="primary"
                  variant="outlined"
                />
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Party Details */}
        {renderPartyDetails("Certifier Details", data.certifierDetails)}
        {renderPartyDetails("Exporter Details", data.exporterDetails)}
        {renderPartyDetails("Producer Details", data.producerDetails)}
        {renderPartyDetails("Importer Details", data.importerDetails)}

        {/* Items */}
        {data.items && data.items.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Items ({data.items?.length || 0})
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {data.items.map((item, index) => (
                  <Box key={index}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: tokens.fontWeight.bold }}>
                        Item {index + 1}
                      </Typography>
                      {item.INCLUDED && (
                        <Chip 
                          label="INCLUDED" 
                          size="small" 
                          color="success"
                          sx={{ fontWeight: tokens.fontWeight.bold }}
                        />
                      )}
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 1, ml: 2 }}>
                      {item.partNumber && (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.bold }}>Part Number:</Typography>
                          <Typography variant="body2">{item.partNumber}</Typography>
                        </>
                      )}
                      {item.description && (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.bold }}>Description:</Typography>
                          <Typography variant="body2">{item.description}</Typography>
                        </>
                      )}
                      {item.origin && (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.bold }}>Origin:</Typography>
                          <Box>
                            <Chip 
                              label={item.origin} 
                              size="small" 
                              sx={{
                                backgroundColor: tokens.status.successBg,
                                color: tokens.status.success,
                                fontWeight: tokens.fontWeight.bold,
                                "& .MuiChip-label": {
                                  px: 1.5,
                                },
                              }}
                            />
                          </Box>
                        </>
                      )}
                      {item.hts && (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.bold }}>HTS Code:</Typography>
                          <Typography variant="body2">{item.hts}</Typography>
                        </>
                      )}
                      {item.originCriterion && (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.bold }}>Origin Criterion:</Typography>
                          <Typography variant="body2">{item.originCriterion}</Typography>
                        </>
                      )}
                      {item.invoiceNumber && (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.bold }}>Invoice Number:</Typography>
                          <Typography variant="body2">{item.invoiceNumber}</Typography>
                        </>
                      )}
                      {item.order && (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.bold }}>Order Qty:</Typography>
                          <Typography variant="body2">{item.order}</Typography>
                        </>
                      )}
                      {item.ship && (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.bold }}>Ship Qty:</Typography>
                          <Typography variant="body2">{item.ship}</Typography>
                        </>
                      )}
                      {item.net && (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.bold }}>Net Weight:</Typography>
                          <Typography variant="body2">{item.net}</Typography>
                        </>
                      )}
                      {item.amount && (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.bold }}>Amount:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.bold, color: tokens.status.success }}>
                            {item.amount}
                          </Typography>
                        </>
                      )}
                      {item.qtyPerPallet && (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.bold }}>Qty per Pallet:</Typography>
                          <Typography variant="body2">{item.qtyPerPallet}</Typography>
                        </>
                      )}
                    </Box>
                    {index < (data.items?.length || 0) - 1 && <Divider sx={{ mt: 2 }} />}
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Summary Info */}
        <Alert severity="info">
          <Typography variant="body2">
            The data has been successfully extracted and saved. You can now view and download the certificate from the table.
          </Typography>
        </Alert>
      </Box>
    </Box>
  );
};

export default ResponseDisplay;